import React from 'react';
import { Box, TextField, InputAdornment, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import { Search } from '@mui/icons-material';
import type { PharmacyMapFiltersProps, SearchType } from './types';

/**
 * Компонент фильтров и поиска для карты аптек
 */
export const PharmacyMapFilters: React.FC<PharmacyMapFiltersProps> = ({
  searchQuery,
  searchType,
  radiusFilter,
  userLocation,
  filteredCount,
  totalCount,
  loadingProducts,
  lastCacheUpdate,
  onSearchChange,
  onSearchTypeChange,
  onRadiusChange
}) => {
  const formatLastUpdate = () => {
    if (!lastCacheUpdate) return 'Загрузка...';

    const now = new Date();
    const diff = Math.floor((now.getTime() - lastCacheUpdate.getTime()) / 1000 / 60); // минуты

    if (diff < 1) return 'только что';
    if (diff < 60) return `${diff} мин. назад`;

    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours} ч. назад`;

    return lastCacheUpdate.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
      {/* Тип поиска */}
      <FormControl size="small" sx={{ width: 130 }}>
        <InputLabel
          sx={{
            color: '#71717a',
            '&.Mui-focused': { color: '#18181b' }
          }}
        >
          Поиск по
        </InputLabel>
        <Select
          value={searchType}
          label="Поиск по"
          onChange={(e) => onSearchTypeChange(e.target.value as SearchType)}
          sx={{
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e4e4e7'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#a1a1aa'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#18181b',
              borderWidth: '1px'
            }
          }}
        >
          <MenuItem value="product">Товар</MenuItem>
          <MenuItem value="pharmacy">Аптека</MenuItem>
        </Select>
      </FormControl>

      {/* Поиск */}
      <TextField
        size="small"
        placeholder={searchType === 'pharmacy' ? 'Поиск по аптеке или адресу...' : 'Поиск товара...'}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: '#a1a1aa' }} />
            </InputAdornment>
          ),
          endAdornment: loadingProducts && searchType === 'product' && searchQuery ? (
            <InputAdornment position="end">
              <CircularProgress size={20} color="inherit" sx={{ color: '#71717a' }} />
            </InputAdornment>
          ) : null,
        }}
        sx={{
          flex: '1 1 300px',
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'white',
            '& fieldset': { borderColor: '#e4e4e7' },
            '&:hover fieldset': { borderColor: '#a1a1aa' },
            '&.Mui-focused fieldset': { borderColor: '#18181b', borderWidth: '1px' }
          }
        }}
      />

      {/* Радиус поиска */}
      {userLocation && (
        <TextField
          size="small"
          type="number"
          label="Радиус поиска (км)"
          value={radiusFilter}
          onChange={(e) => {
            const value = Number(e.target.value);
            onRadiusChange(value < 0 ? 0 : value);
          }}
          inputProps={{ min: 0, step: 1 }}
          sx={{
            width: 180,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: 'white',
              '& fieldset': { borderColor: '#e4e4e7' },
              '&:hover fieldset': { borderColor: '#a1a1aa' },
              '&.Mui-focused fieldset': { borderColor: '#18181b', borderWidth: '1px' }
            },
            '& .MuiInputLabel-root': {
              color: '#71717a',
              '&.Mui-focused': { color: '#18181b' }
            }
          }}
        />
      )}

      {/* Статистика */}
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#71717a' }}>
            Показано: <strong style={{ color: '#09090b' }}>{filteredCount}</strong> из {totalCount}
          </Typography>
        </Box>

        {/* Время последнего обновления */}
        {searchType === 'product' && (
          <Typography variant="caption" sx={{ color: '#a1a1aa', fontFamily: '"Geist Mono", monospace' }}>
            Обновлено: {formatLastUpdate()}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PharmacyMapFilters;

